import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/features/auth/useAuth';
import { useTenantTimeZone } from '@/features/auth/useTenantTimeZone';
import { useOverview, todayRange } from '@/features/overview/useOverview';
import { useFleetAlerts, optsForFilter } from '@/features/alerts/useAlerts';
import { FeedRow } from '@/features/alerts/FeedRow';
import { QueryErrorState } from '@/components/QueryErrorState';
import { hasObservedPosition, isMaintenance } from '@/features/alerts/types';
import type { FeedItem } from '@/features/alerts/types';
import { formatDistance } from '@/features/trips/format';
import { useLiveVehicles } from '@/features/live/useLiveVehicles';
import { useLiveSocketStatus } from '@/features/live/LiveSocketProvider';
import type { LiveVehicle } from '@/features/live/types';
import { hasPosition, markerTone, MOTION_VAR } from '@/features/live/motion';
import { detectLowData } from '@/features/live/lowData';
import { OverviewLiveMap } from '@/features/overview/OverviewLiveMap';
import { FleetSearch } from '@/features/overview/FleetSearch';
import {
  countByState,
  sortByState,
  VEHICLE_STATE_ORDER,
  type StateCounts,
} from '@/features/live/vehicleState';
import { compactStateWord } from '@/features/live/VehicleStateLine';
import { TONE_DOT_COLOR } from '@/features/live/format';
import { formatRelativeAge } from '@/lib/relativeTime';

// The chip/summary swatch colour per state, mapped from the marker palette so the
// dashboard summary reads identically to the map's chips. Palette only, no literal.
const STATE_DOT: Record<(typeof VEHICLE_STATE_ORDER)[number], string> = {
  moving: MOTION_VAR.moving,
  idling: MOTION_VAR.idle,
  parked: MOTION_VAR.stopped,
  engineOn: MOTION_VAR.idle,
  // 15e: uncertainty takes the UNKNOWN swatch, never idle amber.
  engineStale: MOTION_VAR.unknown,
  noContact: MOTION_VAR.offline,
  unknown: MOTION_VAR.unknown,
};

/**
 * The Overview — the first screen after login (canvas 1h, degraded honestly).
 *
 * WHAT CHANGED AND WHY. This screen used to render "No vehicles yet"
 * UNCONDITIONALLY, with a comment that said production had 0 vehicles the day it
 * was written. Slice 05 seeded the pilot's vehicle; nothing re-derived the
 * screen, so the pilot was told he had no vehicles on every login while his
 * Vehicles list showed the truck. A screen that ASSERTS a fact about production
 * state ("there are no vehicles") without QUERYING it is the same defect as a
 * comment describing a guard that never runs — see CLAUDE.md. So the empty state
 * is now CONDITIONAL on a real query, and ci/check-empty-state-data.mjs plus the
 * DOM test in Dashboard.test.tsx keep it that way.
 *
 * WHAT IT SHOWS, all from data we already hold (`/api/vehicles/live` → Redis):
 *   - real counts: total, online, offline, and the four-way motion breakdown;
 *   - a live fleet panel reusing the map list's presentation (dot tone + plate +
 *     motion word + position age) and the same freshness helper — no second copy;
 *   - an attention list derived ONLY from position freshness, asserting nothing
 *     it did not observe (never "parked"/"stopped"/"idle" — just "has not
 *     reported a position in over N h");
 *   - a genuine empty state for a tenant that really has no vehicles.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * SLICE 15 — THE DEBT THIS COMMENT RECORDED IS NOW PAID, AND ONLY WHERE IT CAN BE.
 * ─────────────────────────────────────────────────────────────────────────────
 * The paragraph below used to read: "DROPPED, with no stubs (same rule as the
 * nav): alerts feed (slice 13), distance today (11), fuel, next invoice
 * (billing)." It is kept, not deleted, because the reasoning was right and the
 * list is what changed: slices 11 and 13 shipped, so `Alerts today`, its critical
 * count, `Distance today` and a Recent-alerts panel are here now — sourced, never
 * stubbed. **Fuel, next invoice and the canvas's search box are STILL ABSENT**,
 * for exactly the reason the original note gives.
 *
 * The canvas's embedded live-map panel (canvas 1i: "Live fleet · Open full map ›")
 * now ships — as `OverviewLiveMap`, in **canvas 1i's two-column row beneath the KPI
 * tiles**: the map at 1.6fr on the left, the Recent-alerts rail at 1fr down the
 * right, stacking below `lg`. It shares the very MapCanvas the /map route loads
 * (never a second map), and its maplibre chunk is fetched only when the panel
 * enters the viewport (IntersectionObserver → dynamic `import()`), so the login
 * critical path still carries zero maplibre bytes — the hard blocker for this
 * slice, asserted by ci/check-overview-render.mjs (M-1/M-4).
 *
 * 🔴 AN EARLIER BUILD PLACED IT FULL-WIDTH AND LAST. THAT IS WITHDRAWN, and the
 * reason it was withdrawn is worth more than the layout. It was placed there so
 * the M-4 gate could watch the chunk arrive on scroll at a DESKTOP viewport, where
 * the canvas position would have put the panel above the fold. That is **the
 * instrument dictating the product** — the layout bent to make a test observable.
 * The product defines the layout; the gate had to find another way to observe, and
 * did: M-4 now asserts on the 390×844 phone viewport, which is both where the panel
 * is genuinely below the fold AND where 230 kB of map actually costs the user
 * something. The honest test and the honest layout turned out to be the same
 * change, once the reason was examined instead of accepted.
 *
 * WHERE THE NUMBERS COME FROM, AND WHY IT IS TWO QUERIES AND NOT ONE:
 *   - the vehicle counts and the fleet list read `/api/vehicles/live` → REDIS;
 *   - `Alerts today` / `Distance today` read `/api/overview` → POSTGRES.
 * They degrade independently: a liveness outage blanks the live tiles (we cannot
 * assert what we cannot see) and leaves the day totals standing, because nothing
 * about a Redis outage makes yesterday's trips unknown. Withholding numbers we DO
 * have would be its own dishonesty, in the opposite direction.
 *
 * THE TILE CANNOT DISAGREE WITH THE SCREEN IT LINKS TO. `Alerts today`'s critical
 * count is graded server-side by the SAME predicate the Alerts feed grades with,
 * and `Distance today` sums the SAME trip set the Trips page lists. That is a
 * server-side guarantee by construction (shared SQL fragments), not something this
 * component re-derives — which is why this file contains no severity rule and no
 * window rule of its own.
 *
 * "TODAY" IS RESOLVED IN THE TENANT'S ZONE, never the browser's — see
 * `todayRange`. On this screen more than any other: in Mogadishu the two agree,
 * so a browser-zone bug here would look perfect to everyone who checked it.
 *
 * The original note, unchanged (its list is the part that moved, not its rule):
 * DROPPED, with no stubs (same rule as the nav): alerts feed (slice 13),
 * distance today (11), fuel, next invoice (billing). No greyed tiles, no
 * "coming soon" — a disabled control promises a feature and dates the promise.
 *
 * Every colour is a token (motion tokens live once in motion.ts; SVGs paint
 * `currentColor` off a text-token class), so the queued theme slice re-points
 * them in one place. All four states — loading, error, empty, populated — are
 * first-class; the empty state is never a flash while the query is in flight.
 */

// Position freshness threshold for the attention list. Deliberately its OWN
// number ("over 3 h"), distinct from the map's 120 s stale tone: a fleet manager
// glancing at a landing screen cares about hours-old silence, not seconds. The
// sentence names the hours so the copy and the threshold cannot drift.
const ATTENTION_STALE_HOURS = 3;
const ATTENTION_STALE_SEC = ATTENTION_STALE_HOURS * 3600;

// The landing fleet panel is a glance, not the full list. Bound the DOM (the
// performance budget: never render thousands of nodes) and point to the
// clustered live map for the rest.
const FLEET_PREVIEW_LIMIT = 6;

// How many crossings the Recent-alerts panel shows. A glance, not the feed — the
// same bound-the-DOM reasoning as FLEET_PREVIEW_LIMIT, and the panel links to the
// full screen for the rest.
const RECENT_ALERTS_LIMIT = 4;

// ── Stat tile ────────────────────────────────────────────────────────────────
// `value` is a STRING, not a number: `Distance today` renders "3.5 km" through the
// shared `formatDistance`, and a tile that could only hold an integer would have
// pushed that formatting into JSX or, worse, into a second unit conversion here.
// `sub` carries the canvas's second line ("3 critical"); it renders only when it
// has something to say, so a day with no critical crossings shows no empty row.
function StatTile({
  label,
  value,
  sub,
  accent,
  testId,
  to,
  subTone = 'warn',
}: {
  label: string;
  value: string;
  sub?: string | null;
  accent?: boolean;
  testId?: string;
  /**
   * D6 — where this tile navigates. **Omit it and the tile is INERT, deliberately.**
   *
   * Canvas 1i puts a link affordance (`›`) on exactly two elements, and the KPI
   * tiles are not among them; D6 adds navigation anyway, because on a phone a
   * number with a page behind it that does not respond to touch reads as broken.
   *
   * But only where there IS a page. `Fuel events` and `Plan` deliberately do NOT
   * receive a `to`: **a tappable control with nothing behind it is worse than an
   * inert number** — it promises a destination that does not exist, which is the
   * disabled-control rule wearing a different costume. The asymmetry is the
   * decision, not an oversight.
   */
  to?: string;
  /** `warn` for the critical count; `muted` for a neutral sub-line like the plan's. */
  subTone?: 'warn' | 'muted';
}) {
  const body = (
    <>
      <p className={`num text-2xl font-bold ${accent ? 'text-raad-ok' : 'text-raad-text'}`}>{value}</p>
      <p className="mt-0.5 text-xs text-raad-muted">{label}</p>
      {sub ? (
        <p className={`mt-0.5 text-[11px] font-semibold ${subTone === 'warn' ? 'text-raad-warn' : 'text-raad-dim'}`}>
          {sub}
        </p>
      ) : null}
    </>
  );

  const shell = 'rounded-panel border border-raad-border bg-raad-panel px-4 py-3.5';

  // A real anchor, not a div with an onClick: it gets keyboard focus, a real
  // pointer affordance and browser-native navigation semantics for free, and the
  // gate can assert `location.pathname` actually moved.
  if (to) {
    return (
      <Link
        to={to}
        data-testid={testId}
        className={`${shell} block transition-colors hover:border-raad-line-20 hover:bg-raad-fill-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-raad-accent`}
      >
        {body}
      </Link>
    );
  }
  return (
    <div className={shell} data-testid={testId}>
      {body}
    </div>
  );
}

// ── Phone status chip (56g) ───────────────────────────────────────────────────
// The compact, tappable status count that REPLACES the KPI tiles on the phone
// Overview (Mahad's "compact stats + map" layout). One thin horizontally-scrolling
// row of these sits above the live map. Each reuses a KPI destination, so the phone
// and desktop lead to the same screens. The Alerts chip carries the critical count
// as a warn badge. `value` is a string (it may be "—" before the summary lands, or a
// formatted "12.4 km"), exactly like `StatTile`.
function StatChip({
  to,
  label,
  value,
  accent = false,
  badge = null,
  badgeLabel,
  testId,
}: {
  to: string;
  label: string;
  value: string;
  accent?: boolean;
  badge?: number | null;
  badgeLabel?: string;
  testId: string;
}) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex flex-none items-center gap-2 rounded-full border border-raad-border bg-raad-panel px-3.5 py-2 transition-colors hover:border-raad-line-20 hover:bg-raad-fill-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-raad-accent"
    >
      <span className={`num text-sm font-bold ${accent ? 'text-raad-ok' : 'text-raad-text'}`}>{value}</span>
      <span className="whitespace-nowrap text-xs text-raad-muted">{label}</span>
      {badge != null && badge > 0 ? (
        <span
          aria-label={badgeLabel}
          className="ml-0.5 min-w-4 rounded-full bg-raad-warn px-1 text-center text-[10px] font-bold leading-4 text-raad-onprimary"
        >
          {badge}
        </span>
      ) : null}
    </Link>
  );
}

// A tile whose number has not arrived yet. NOT a zero: "0 alerts today" is a
// claim about the fleet, and rendering it while the query is still in flight is
// the unconditional-empty-state defect this screen already has a CI guard for
// (ci/check-empty-state-data.mjs). An em-dash says "not yet known", which is true.
function PendingTile({ label, testId }: { label: string; testId?: string }) {
  return <StatTile label={label} value="—" testId={testId} />;
}

// ── Vehicle-state breakdown (read-only, not the map's interactive filter) ─────
// The ONE vehicle-state vocabulary (13e), one count per state, reading identically
// to the map's chips — but here a summary, so nothing to toggle. The label-less
// states carry their measurement/tracker framing; "Unknown" never appears.
/**
 * The "Right now" strip — ONLY states with a non-zero count.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY, AND THE EVIDENCE IS A REAL MISREAD.
 * ─────────────────────────────────────────────────────────────────────────────
 * It used to render all six states including zeros: at one vehicle that is five
 * counts of nothing surrounding one fact. **A count of zero carries no
 * information** — "0 No recent fix" tells a fleet manager nothing they can act on,
 * and the row still costs a coloured dot and a word.
 *
 * The cost was not merely noise. Mahad read the purple dot beside "0 No recent
 * fix" as belonging to "1 Parked" two rows away, and reported the parked colour as
 * wrong. It was not — measured, "1 Parked" renders `#44708F` exactly as D1
 * specifies. **The layout created a false reading of correct data**, which is the
 * kind of defect no assertion about colour values would ever have caught.
 *
 * At one vehicle the strip is now simply "1 Parked", which is the whole truth.
 *
 * 🔴 IT MUST NEVER RENDER EMPTY. All-zero is itself meaningful and gets its own
 * honest line rather than a blank bordered row. In practice the counts sum to the
 * vehicle count and this branch is unreachable while any vehicle exists — but
 * "unreachable" is exactly what the empty-state defects in this project's history
 * were assumed to be, so it is written rather than argued.
 */
function StateSummary({ counts, t }: { counts: StateCounts; t: TFunction }) {
  const present = VEHICLE_STATE_ORDER.filter((s) => counts[s] > 0);

  if (present.length === 0) {
    return (
      <div className="rounded-panel border border-raad-border bg-raad-panel px-4 py-3">
        <p className="text-xs text-raad-muted">{t('dashboard.activity.noState')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-panel border border-raad-border bg-raad-panel px-4 py-3">
      {present.map((s) => (
        <span key={s} className="inline-flex items-center gap-1.5">
          <span
            className="size-2 flex-none rounded-full"
            style={{ backgroundColor: STATE_DOT[s] }}
            aria-hidden="true"
          />
          <span className="num text-sm font-bold text-raad-text">{counts[s]}</span>
          <span className="text-xs text-raad-muted">{t(`vehicleState.${s}`)}</span>
        </span>
      ))}
    </div>
  );
}

// ── Attention list (position freshness only) ─────────────────────────────────
function AttentionPanel({ staleCount, t }: { staleCount: number; t: TFunction }) {
  if (staleCount === 0) {
    return (
      <p className="flex items-center gap-2 text-sm text-raad-muted">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-none text-raad-ok" aria-hidden="true">
          <path d="M20 6 9 17l-5-5" />
        </svg>
        {t('dashboard.attention.allClear')}
      </p>
    );
  }
  return (
    <div
      role="status"
      className="flex items-start gap-2.5 rounded-panel border border-raad-warn/25 bg-raad-warn/10 px-4 py-3"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="mt-0.5 flex-none text-raad-warn" aria-hidden="true">
        <path d="M12 9v4 M12 17h.01 M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      </svg>
      <p className="text-sm text-raad-text">
        {t('dashboard.attention.stalePosition', { count: staleCount, hours: ATTENTION_STALE_HOURS })}
      </p>
    </div>
  );
}

// ── Fleet row ────────────────────────────────────────────────────────────────
// The SAME presentation as the live map's compact list row (dot tone + plate +
// position age + tone-driven motion word), wrapped as a link into the vehicle
// detail that slice 09 shipped. The freshness value is the server-computed
// `positionAgeSec` rendered through the shared `formatRelativeAge` helper — never
// recomputed from a timestamp, so a wrong phone clock cannot drift it. The full
// three-state contact-vs-position sentence lives in the detail you tap into, as
// it does for the map list — this stays a glance, and swaps trivially if that
// wording ever moves up into the row.
function FleetRow({ v, t }: { v: LiveVehicle; t: TFunction }) {
  const tone = markerTone(v);
  const located = hasPosition(v);
  const age = v.positionAgeSec != null ? formatRelativeAge(t, v.positionAgeSec) : null;

  return (
    <li>
      <Link
        to={`/vehicles/${v.id}`}
        className="flex items-center gap-2.5 border-b border-raad-line-5 px-4 py-3 last:border-b-0 hover:bg-raad-fill-3"
      >
        <span
          className={`size-2.5 flex-none rounded-full ${located ? '' : 'bg-transparent ring-1 ring-inset ring-raad-dim'}`}
          style={located ? { backgroundColor: TONE_DOT_COLOR[tone] } : undefined}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline justify-between gap-2">
            <span className="truncate font-display text-sm font-bold text-raad-text">{v.plate}</span>
            {age ? <span className="num flex-none text-[11px] text-raad-dim">{age}</span> : null}
          </span>
          {(() => {
            const w = compactStateWord(t, v, located);
            return (
              <span className={`mt-0.5 block truncate text-xs ${w?.className ?? 'text-raad-muted'}`}>
                {w?.text ?? ''}
              </span>
            );
          })()}
        </span>
      </Link>
    </li>
  );
}

function FleetPanel({ vehicles, t }: { vehicles: LiveVehicle[]; t: TFunction }) {
  const sorted = sortByState(vehicles);
  const shown = sorted.slice(0, FLEET_PREVIEW_LIMIT);
  const overflow = sorted.length - shown.length;

  return (
    <section>
      <div className="mb-2.5 flex items-baseline justify-between gap-3">
        <h2 className="font-display text-sm font-bold text-raad-text">{t('dashboard.fleet.heading')}</h2>
        <Link to="/map" className="text-xs font-semibold text-raad-accent hover:underline">
          {t('dashboard.fleet.seeAll')}
        </Link>
      </div>
      <ul className="overflow-hidden rounded-panel border border-raad-border bg-raad-panel">
        {shown.map((v) => (
          <FleetRow key={v.id} v={v} t={t} />
        ))}
      </ul>
      {overflow > 0 ? (
        <Link to="/map" className="mt-2 block text-center text-xs font-medium text-raad-muted hover:text-raad-text">
          {t('dashboard.fleet.more', { count: overflow })}
        </Link>
      ) : null}
    </section>
  );
}

// ── Recent alerts (canvas 1i: "Recent alerts · All alerts ›") ─────────────────
//
// REUSES `AlertRow` — the same component the Alerts screen renders, so a crossing
// reads identically in both places: same verb, same purpose tag, same unread
// weight, same unobserved-gap badge. A second, simpler alert renderer here is
// exactly how two surfaces start describing the same event differently.
//
// TAPPING A ROW DEEP-LINKS WITH THE EVENT'S OWN COORDINATES —
// `/alerts?event=<id>&lat=<observedLat>&lon=<observedLon>` — the same contract the
// push notification uses (ADR 0011 §2). The coordinates come off the ALERT ROW, so
// the map centres on where the crossing happened, never on where the vehicle is
// now. Reading live position here would be the same defect through a different
// door, and it would look correct in every test where the vehicle had not moved.
function RecentAlertsPanel({
  alerts,
  plateById,
  locale,
  timeZone,
  t,
}: {
  alerts: FeedItem[];
  plateById: Map<string, string>;
  locale: string;
  timeZone: string;
  t: TFunction;
}) {
  const navigate = useNavigate();
  const shown = alerts.slice(0, RECENT_ALERTS_LIMIT);

  // A bordered CARD with the header INSIDE it (canvas 1i draws "Recent alerts" as
  // one continuous panel, not a bare heading above unwrapped rows). On lg it fills
  // the live-fleet row's height so it sits level with the map; on the phone it is a
  // fixed-height card that stacks below the map (D7).
  return (
    <div
      data-testid="recent-alerts"
      className="flex h-72 flex-col overflow-hidden rounded-panel border border-raad-border bg-raad-panel lg:h-full"
    >
      <div className="flex flex-none items-center justify-between gap-3 border-b border-raad-border px-4 py-3">
        <h2 className="font-display text-sm font-bold text-raad-text">{t('dashboard.alerts.heading')}</h2>
        <Link to="/alerts" className="text-xs font-semibold text-raad-accent hover:underline">
          {t('dashboard.alerts.seeAll')} <span aria-hidden="true">›</span>
        </Link>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2.5">
        {shown.length === 0 ? (
          // The honest empty state for a real, answered query — "nothing crossed a
          // zone today", not "you have no alerts feature".
          <p className="px-1.5 py-1.5 text-sm text-raad-muted">{t('dashboard.alerts.none')}</p>
        ) : (
          <ul className="space-y-2">
            {shown.map((a) => (
              <FeedRow
                key={a.id}
                item={a}
                plate={plateById.get(a.vehicleId) ?? null}
                locale={locale}
                timeZone={timeZone}
                selected={false}
                onSelect={(alert) =>
                  // 🔴 ONLY A POSITIONED ITEM GOES TO THE ALERTS MAP. A maintenance
                  // reminder and (13f) a tracker-health event have NO location — the
                  // frozen coordinates the other rows carry do not exist for them,
                  // and they must never be given the vehicle's live position instead.
                  // `hasObservedPosition` is what proves the coordinates exist; the
                  // coordinate-less items deep-link to the vehicle (maintenance to
                  // its tab). NOTE the tracker-health row is a Link and ignores this
                  // handler at runtime, but the branch keeps the union exhaustive.
                  navigate(
                    hasObservedPosition(alert)
                      ? `/alerts?event=${encodeURIComponent(alert.id)}&lat=${alert.observedLat}&lon=${alert.observedLon}`
                      : isMaintenance(alert)
                        ? `/vehicles/${alert.vehicleId}?tab=maintenance`
                        : `/vehicles/${alert.vehicleId}`,
                  )
                }
                t={t}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── States ───────────────────────────────────────────────────────────────────
function DegradedBanner({ t }: { t: TFunction }) {
  return (
    <div
      role="status"
      className="flex items-start gap-2.5 rounded-panel border border-raad-warn/25 bg-raad-warn/10 px-4 py-3"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="mt-0.5 flex-none text-raad-warn" aria-hidden="true">
        <path d="M12 9v4 M12 17h.01 M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      </svg>
      <p className="text-sm text-raad-muted">{t('vehicles.degraded')}</p>
    </div>
  );
}

function SkeletonState() {
  // Never a blank main area, and never a flash of "No vehicles yet", while the
  // query is in flight — three shimmering placeholders matching the layout.
  return (
    <div aria-hidden="true" className="space-y-5">
      <div className="grid grid-cols-3 gap-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-[76px] rounded-panel border border-raad-border bg-raad-panel" />
        ))}
      </div>
      <div className="h-12 rounded-panel border border-raad-border bg-raad-panel" />
      <div className="space-y-2 rounded-panel border border-raad-border bg-raad-panel p-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-8 animate-pulse rounded bg-raad-fill-5" />
        ))}
      </div>
    </div>
  );
}

function EmptyState({ t }: { t: TFunction }) {
  return (
    <section className="rounded-panel border border-raad-border bg-raad-panel px-6 py-12 text-center sm:py-16">
      <span className="mx-auto flex size-16 items-center justify-center rounded-full bg-raad-primary/12" aria-hidden="true">
        <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-raad-accent">
          <path d="M1 3h15v13H1z M16 8h4l3 3v5h-7z" />
          <circle cx="5.5" cy="19" r="2" />
          <circle cx="18.5" cy="19" r="2" />
        </svg>
      </span>
      <h2 className="mt-5 font-display text-lg font-bold">{t('dashboard.empty.title')}</h2>
      <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-raad-muted">{t('dashboard.empty.body')}</p>
      <p className="mx-auto mt-4 max-w-md text-xs text-raad-dim">{t('dashboard.empty.hint')}</p>
    </section>
  );
}

// ── Screen ────────────────────────────────────────────────────────────────────
export function Dashboard() {
  const { t, i18n } = useTranslation();
  const { state } = useAuth();
  const tenantId = state.status === 'authenticated' ? state.user.tenantId : undefined;
  // 56g — the Overview map is now LIVE. It reads the SAME query cache the Live map
  // reads, fed by the ONE app-level socket (LiveSocketProvider). Passing
  // `socketHealthy` suppresses the 60 s poll while the socket is open, so the
  // Overview receives the same per-second frames the Live map does — one socket,
  // one model, two screens.
  const socket = useLiveSocketStatus();
  const query = useLiveVehicles({ tenantId, socketHealthy: socket.status === 'open' });

  // "Today" in the TENANT's zone (see todayRange). Memoised on the zone alone: a
  // new object every render would change the query key every render and refetch
  // forever. The minute-resolution `to` bound is intentionally NOT recomputed on a
  // timer — the 60 s refetch interval carries freshness, and a window that moved
  // on every render would defeat caching entirely.
  const { timeZone } = useTenantTimeZone();
  const range = useMemo(() => todayRange(Date.now(), timeZone), [timeZone]);

  // No `enabled` gate on either: this route renders only inside `RequireAuth`, so
  // an unauthenticated render is not a state that exists here. The Alerts screen
  // calls the same hook the same way.
  const overview = useOverview(range);
  // The recent-alerts panel reads the FEED, not the summary: the summary carries
  // counts only (it is a summary), and the rows must be the same rows the Alerts
  // screen would show. `optsForFilter('all')` — both severities, because a
  // "returned to base" event is worth seeing at a glance even though it never
  // pushes and never counts as critical.
  const alertsQuery = useFleetAlerts(range, null, optsForFilter('all'));

  const vehicles = query.data?.vehicles ?? [];
  const degraded = query.data?.liveness.degraded ?? false;

  // Read once — the metered/save-data signal the embedded map uses to suppress
  // vector tiles (markers still draw). detectLowData reads navigator only, so it
  // is stable for the session; a mid-session change is not worth a re-render.
  const lowData = useMemo(() => detectLowData(), []);

  // Counts derived from live state. Under a liveness outage every status is
  // 'unknown', so online/offline/motion/attention are NOT asserted — we show the
  // degraded banner and the count we DO know (the list itself comes from our own
  // DB), never a fabricated "0 online".
  const total = vehicles.length;
  // Tracker-state count. Canvas 1i renders ONE tile — "Online 31 / 42" — so the
  // separate Reporting / Not reporting tiles are gone; the full breakdown still
  // lives in the state summary below, which is where a fleet manager reads detail.
  const reporting = vehicles.filter((v) => v.status === 'online').length;
  const stateCounts = countByState(vehicles);
  const staleCount = vehicles.filter(
    (v) => v.positionAgeSec != null && v.positionAgeSec > ATTENTION_STALE_SEC,
  ).length;

  // The greeting and its "fleet is N% online" status line moved to the shell's top
  // bar this slice (canvas 1i draws greeting + search + bell in one bar). The D2
  // percentage/count rule moved WITH it, once, into `OverviewGreeting` — not copied.

  // Plates for the alert rows, joined from the live list this screen already
  // loads — no second vehicles query. A trip/alert carries `vehicleId`; a plate is
  // mutable vehicle identity and is never copied onto history (S1).
  // Keyed on the QUERY's array, not the `?? []` fallback: the fallback is a new
  // array literal every render, which would make this memo recompute every time
  // and hand `RecentAlertsPanel` a new Map identity on each pass — a memo that
  // costs and buys nothing. `query.data?.vehicles` is referentially stable between
  // fetches, so this recomputes exactly when the fleet actually changes.
  const plateById = useMemo(
    () => new Map((query.data?.vehicles ?? []).map((v) => [v.id, v.plate])),
    [query.data?.vehicles],
  );

  /**
   * The two day tiles, shared by the healthy and degraded branches because they do
   * not depend on liveness at all.
   *
   * THREE DISTINCT STATES, and none of them is allowed to render as another:
   *   - in flight → "—". NOT "0": a zero is a claim about the fleet, and claiming
   *     it before the query answers is the unconditional-empty-state defect this
   *     screen already carries a CI guard for;
   *   - failed → "—" as well, for the same reason. The screen does not go red for
   *     a KPI: the live map, the fleet list and the alerts feed are all still
   *     usable, and a whole-screen error for a summary tile would be a worse lie
   *     than the missing number;
   *   - answered → the real value, INCLUDING a real zero, which is a perfectly
   *     good answer for a quiet morning and is rendered as one.
   */
  const dayTiles = overview.data ? (
    <>
      <StatTile
        testId="tile-alerts-today"
        to="/alerts"
        label={t('dashboard.stats.alertsToday')}
        value={String(overview.data.alerts.total)}
        sub={
          overview.data.alerts.critical > 0
            ? t('dashboard.stats.criticalCount', { count: overview.data.alerts.critical })
            : null
        }
      />
      <StatTile
        testId="tile-distance-today"
        to="/trips"
        label={t('dashboard.stats.distanceToday')}
        value={formatDistance(t, overview.data.trips.distanceM)}
      />
    </>
  ) : (
    <>
      <PendingTile testId="tile-alerts-today" label={t('dashboard.stats.alertsToday')} />
      <PendingTile testId="tile-distance-today" label={t('dashboard.stats.distanceToday')} />
    </>
  );

  /**
   * The two tiles that hold no live measurement — canvas 1i's fifth and sixth
   * slots. Both render at full grid position and dimensions so the six-column
   * layout is faithful; **neither is a link** (see `StatTile.to`), because there
   * is no page behind either.
   *
   * `Fuel events` — the HARDWARE cannot produce it. No LLS sensor on the FMC920;
   * fuel needs FMC130/650. Not "no events today": *no sensor*. It stays empty on
   * this tracker however long you wait, so the tile says the cause.
   *
   * `Plan` — canvas 1i draws "Next invoice · $420 · due 10 Jul". We ship the RATE
   * instead, because the rate is a fact we hold and an invoice is an event that
   * has not happened: no invoicing system exists to make "due 10 Sep" true. The
   * amount is real (Mahad confirmed the pilot pays $6/month) and comes from
   * `tenants.monthly_rate_cents` via `/api/me` — never a constant in this file.
   *
   * 🔴 `null`/absent rate renders the NO-CLAIM state, never `$0`. The column is
   * nullable with no default precisely so an unset rate stays visibly unset; a
   * `$0` here would assert the customer pays nothing.
   *
   * The sub-line says `total · N vehicles`, NOT `N vehicles`. The value is a
   * TENANT-level amount and nothing multiplies — but "$6 / month" over "1 vehicle"
   * reads as *per vehicle*, and at n=1 both readings give $6, so the ambiguity is
   * invisible on exactly the data we have and wrong for the first three-vehicle
   * tenant. One word removes it permanently.
   */
  const rateCents =
    state.status === 'authenticated' ? state.user.monthlyRateCents ?? null : null;

  /**
   * 🔴 THE "FUEL EVENTS" TILE WAS HERE AND IS GONE (17o).
   *
   * It rendered `—` with the sub-line "Needs a fuel sensor", on the first screen
   * after login, in both languages. Fuel does not exist and the FMC920 CANNOT
   * produce it — no CAN, no RS232 (CLAUDE.md: "Don't promise fuel analytics on
   * the entry tier"). `fuel_events` holds zero rows and nothing writes to it.
   *
   * It was kept for grid fidelity to canvas 1i and made honest — inert, not a
   * link, naming the cause instead of faking a count. That is a better version
   * of the wrong thing. The rule is ABSENCE OVER DISABLED: a control for a
   * feature that does not exist promises it and dates the promise, and this one
   * additionally marketed hardware we do not sell. Same family as slice 05's
   * unfillable columns and slice 09's disabled sidebar items.
   *
   * Found by the 28 Aug stocktake as the single customer-visible violation in
   * the whole app; the nav lists were clean. When fuel ships, its tile ships
   * with it.
   */
  // The plan value + sub, computed ONCE so the desktop tile and the phone Plan card
  // (56g, below the map) cannot show different money.
  const planValue = rateCents != null ? t('dashboard.stats.planValue', { amount: (rateCents / 100).toFixed(0) }) : '—';
  const planSub = rateCents != null ? t('dashboard.stats.planSub', { count: total }) : t('dashboard.stats.planNoRate');

  const staticTiles = (
    <>
      <StatTile
        testId="tile-plan"
        label={t('dashboard.stats.plan')}
        value={planValue}
        sub={planSub}
        subTone="muted"
      />
    </>
  );

  // 56g — the phone Plan card, shown BELOW the live map (desktop keeps the tile in
  // the KPI row). Same values as the desktop tile, inert (no page behind a rate).
  const phonePlanCard = (
    <div className="lg:hidden">
      <StatTile label={t('dashboard.stats.plan')} value={planValue} sub={planSub} subTone="muted" />
    </div>
  );

  // 56g — the phone status chips that REPLACE the KPI tiles below `lg`. One thin
  // horizontally-scrollable row above the map. Each reuses the KPI destination:
  // Online→/map, Moving→/map?status=moving, Alerts→/alerts, Distance→/trips. The
  // `-mx-5 px-5` lets the row scroll edge-to-edge inside the page's side padding, so
  // a long Somali label scrolls rather than clipping (measured at 390px, EN + SO).
  const phoneChips = (
    <div
      data-testid="overview-chips"
      className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1 lg:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <StatChip
        testId="chip-online"
        to="/map"
        label={t('dashboard.chips.online')}
        value={t('dashboard.stats.onlineValue', { online: reporting, total })}
        accent
      />
      <StatChip
        testId="chip-moving"
        to="/map?status=moving"
        label={t('dashboard.chips.moving')}
        value={String(stateCounts.moving)}
      />
      <StatChip
        testId="chip-alerts"
        to="/alerts"
        label={t('dashboard.chips.alerts')}
        value={overview.data ? String(overview.data.alerts.total) : '—'}
        badge={overview.data ? overview.data.alerts.critical : null}
        badgeLabel={overview.data ? t('dashboard.stats.criticalCount', { count: overview.data.alerts.critical }) : undefined}
      />
      <StatChip
        testId="chip-distance"
        to="/trips"
        label={t('dashboard.chips.distance')}
        value={overview.data ? formatDistance(t, overview.data.trips.distanceM) : '—'}
      />
    </div>
  );

  // 56g — the degraded phone chips: online/moving are WITHHELD under a liveness
  // outage (we cannot assert them), but the fleet size and the day totals are known
  // (the latter from Postgres, unaffected by a Redis outage), so those three still
  // show. Desktop keeps its full degraded tile row.
  const degradedPhoneChips = (
    <div
      data-testid="overview-chips"
      className="-mx-5 flex gap-2 overflow-x-auto px-5 pb-1 lg:hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    >
      <StatChip testId="chip-total" to="/map" label={t('dashboard.stats.total')} value={String(total)} accent />
      <StatChip
        testId="chip-alerts"
        to="/alerts"
        label={t('dashboard.chips.alerts')}
        value={overview.data ? String(overview.data.alerts.total) : '—'}
        badge={overview.data ? overview.data.alerts.critical : null}
        badgeLabel={overview.data ? t('dashboard.stats.criticalCount', { count: overview.data.alerts.critical }) : undefined}
      />
      <StatChip
        testId="chip-distance"
        to="/trips"
        label={t('dashboard.chips.distance')}
        value={overview.data ? formatDistance(t, overview.data.trips.distanceM) : '—'}
      />
    </div>
  );

  /**
   * The recent-alerts panel, rendered only once the feed has ANSWERED.
   *
   * While it is loading or failed we render nothing at all rather than an empty
   * panel: "no alerts today" and "we have not asked yet" are different statements,
   * and the panel's own empty copy says the first one.
   */
  const recentAlerts = alertsQuery.data ? (
      <RecentAlertsPanel
        alerts={alertsQuery.data.alerts}
        plateById={plateById}
        locale={i18n.language}
        timeZone={timeZone}
        t={t}
      />
    ) : null;

  /*
   * FIX 2 — NO MAX-WIDTH. Canvas 1i fills from the sidebar to the right edge
   * with page padding only. `max-w-3xl` was the single cause of everything
   * downstream: on a 1905 px screen the content sat in a 768 px column, the six
   * tiles cramped enough to wrap mid-value, and the map pane came out ~470 px
   * wide against a canvas that gives it far more.
   *
   * SLICE 15d — THE MAP/ALERTS ROW SITS DIRECTLY BENEATH THE TILES AND FILLS THE
   * REMAINING VIEWPORT HEIGHT (canvas 1i). On the deployed build "Right now" and
   * "Needs attention" sat BETWEEN the tiles and the row, squashing the map to 28%
   * of the screen. They are honest state the canvas predates, so they are not
   * dropped — they MOVE BELOW the row. The row's `lg` height is
   * `100dvh - <shell chrome> - <the space above it>`, so the map and the alerts
   * card (equal height, one grid row) reach the bottom of the first screenful and
   * the sections below scroll into view. `--raad-chrome` is published by the shell
   * and re-measured when the greeting/offline bar changes, so the number is never
   * a magic constant. Below `lg` the two panes stack (D7) at fixed heights.
   */
  const mobileSearch = (
    // The phone keeps its search in the content body — the mobile top bar is too
    // narrow to carry it beside the greeting. Desktop's search is in the shell bar
    // (canvas 1i), so this is `lg:hidden`, not a second search on the big screen.
    <div className="lg:hidden">
      <FleetSearch vehicles={vehicles} t={t} />
    </div>
  );

  const liveFleetRow = (
    <div
      data-testid="live-fleet-row"
      className="grid grid-cols-1 gap-5 lg:h-[calc(100dvh-var(--raad-chrome,80px)-13rem)] lg:min-h-[420px] lg:grid-cols-[1.6fr_1fr] lg:grid-rows-[minmax(0,1fr)]"
    >
      <OverviewLiveMap vehicles={vehicles} lowData={lowData} t={t} />
      {recentAlerts}
    </div>
  );

  return (
    <div className="px-5 py-8 sm:px-6 lg:py-8">
      {query.isPending ? (
        <>
          <span className="sr-only" role="status">{t('dashboard.loading')}</span>
          <SkeletonState />
        </>
      ) : query.isError ? (
        <QueryErrorState
          error={query.error}
          onRetry={() => void query.refetch()}
          title={t('dashboard.error.title')}
          body={t('dashboard.error.body')}
          retryLabel={t('dashboard.error.retry')}
          variant="panel"
        />
      ) : total === 0 ? (
        <EmptyState t={t} />
      ) : degraded ? (
        // Honest degraded overview: the count we know + the fleet, under the
        // banner. No online/offline/motion/attention — we cannot assert them.
        <div className="space-y-5">
          <DegradedBanner t={t} />
          {mobileSearch}
          {/* 56g — phone: chips replace the tiles (online/moving withheld under a
              liveness outage); desktop keeps its full degraded tile row. */}
          {degradedPhoneChips}
          {/* The live tiles are withheld — but the day totals are NOT. They come
              from Postgres and a Redis outage tells us nothing about them. */}
          <div className="hidden gap-3 lg:grid lg:grid-cols-6" data-testid="kpi-row">
            <StatTile label={t('dashboard.stats.total')} value={String(total)} />
            {dayTiles}
            {staticTiles}
          </div>
          {liveFleetRow}
          {phonePlanCard}
          <FleetPanel vehicles={vehicles} t={t} />
        </div>
      ) : (
        <div className="space-y-5">
          {mobileSearch}
          {/* 56g — PHONE: a thin row of tappable chips replaces the KPI tiles, and
              the map fills the first screen below it. DESKTOP is byte-for-byte
              unchanged: the six-column tile grid is `hidden lg:grid`. */}
          {phoneChips}
          <div className="hidden gap-3 lg:grid lg:grid-cols-6" data-testid="kpi-row">
            <StatTile
              testId="tile-online"
              label={t('dashboard.stats.online')}
              value={t('dashboard.stats.onlineValue', { online: reporting, total })}
              accent
            />
            <StatTile
              testId="tile-moving"
              label={t('dashboard.stats.movingNow')}
              value={String(stateCounts.moving)}
            />
            {dayTiles}
            {staticTiles}
          </div>

          {/* The map/alerts row directly beneath the tiles (canvas 1i). On the phone
              the map fills the first screen; the Plan card and the rest sit below. */}
          {liveFleetRow}

          {/* 56g — the phone Plan card, below the map. */}
          {phonePlanCard}

          {/* "Right now" and "Needs attention" — honest live state the canvas
              predates — now sit BELOW the map row, where slice 15d moved them out
              from between the tiles and the map. */}
          <section>
            <h2 className="mb-2.5 font-display text-sm font-bold text-raad-text">{t('dashboard.activity.heading')}</h2>
            <StateSummary counts={stateCounts} t={t} />
          </section>

          <section>
            <h2 className="mb-2.5 font-display text-sm font-bold text-raad-text">{t('dashboard.attention.heading')}</h2>
            <AttentionPanel staleCount={staleCount} t={t} />
          </section>

          <FleetPanel vehicles={vehicles} t={t} />
        </div>
      )}
    </div>
  );
}
